import { useState, useEffect, useRef, type Ref } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { DEFAULT_BUSINESS_TIME_ZONE } from "@zapis24/utils";
import {
  DEFAULT_PRE_VISIT_REMINDER_INTRO,
  DEFAULT_PRE_VISIT_REMINDER_MESSAGE,
  DEFAULT_RETURN_REMINDER_MESSAGE,
  PAYMENT_BANKS,
  PAYMENT_BANK_LABELS,
  normalizePaymentBanks,
  type PaymentBank,
} from "@zapis24/types";
import { API_BASE, apiFetch } from "../api.js";
import { ScheduleSettingsSection } from "./ScheduleSettingsSection.js";
import {
  needsSavedCityTokenResolution,
  pickExactCitySuggestion,
} from "../settingsLocation.js";

type BillingRequestStatus = "NEW" | "CONTACTED" | "WAITING_PAYMENT" | "ACTIVE" | "REJECTED" | "CANCELLED";
type ActivationSource = "PAYMENT" | "MANUAL";
type SettingsFocusSection = "schedule" | "notifications" | "payment";

interface Business {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  city: string | null;
  timeZone: string;
  citySelectionToken?: string | null;
  address: string | null;
  addressSelectionToken?: string | null;
  locationStatus: "VERIFIED" | "NO_ADDRESS" | null;
  planStatus: string;
  paymentPhone: string | null;
  paymentBank: "SBERBANK" | "TBANK" | null;
  paymentBanks: PaymentBank[];
  paymentOtherBankName: string | null;
  bookingWindowDays: number;
  featureReminder2h: boolean;
  reminder2hMinutesBefore: number;
  reminder2hMessage: string | null;
  featureReturnReminder: boolean;
  returnReminderMessage: string | null;
  masterNotificationChannel: "TELEGRAM" | "MAX" | null;
  masterNotificationBindings?: {
    telegram?: { connected: boolean };
    max?: { connected: boolean };
  };
  billingRequest?: {
    id: string;
    status: BillingRequestStatus;
    activationSource: ActivationSource | null;
    createdAt: string;
    updatedAt: string;
  } | null;
}

interface MaxNotificationBindingCode {
  code: string;
  payload: string;
  expiresAt: string;
}

interface GeoSuggestion {
  id: string;
  label: string;
  description: string;
  provider: "DADATA";
  providerId: string;
  token: string;
  regionId?: string;
  precision?: "street" | "house";
  lat?: number;
  lon?: number;
}

const MAX_BOT_URL = getOptionalBotUrl(
  import.meta.env["VITE_MAX_BOT_URL"] as string | undefined,
  "https://max.ru/id550113317815_bot"
);
const TELEGRAM_ADMIN_BOT_URL = getOptionalBotUrl(
  import.meta.env["VITE_ADMIN_BOT_URL"] as string | undefined,
  "https://t.me/ZapisProAdminBot"
);
const REMINDER_TIME_OPTIONS: { label: string; minutes: number }[] = [
  { label: "1 ч", minutes: 60 },
  { label: "2 ч", minutes: 120 },
  { label: "4 ч", minutes: 240 },
  { label: "12 ч", minutes: 720 },
  { label: "24 ч", minutes: 1440 },
];

function getOptionalBotUrl(envValue: string | undefined, fallback: string): string | null {
  const value = envValue ?? fallback;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toLowerCase();
  if (
    normalized === "disabled" ||
    normalized.includes("replace-with") ||
    normalized.includes("placeholder")
  ) {
    return null;
  }

  return trimmed;
}

function buildUrlWithParams(baseUrl: string, params: Record<string, string>): string {
  try {
    const url = new URL(baseUrl);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
    return url.toString();
  } catch {
    const base = baseUrl.replace(/\/$/, "");
    const search = new URLSearchParams(params).toString();
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${search}`;
  }
}

function minutesToLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} мин`;
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours} ч` : `${Math.floor(hours)} ч ${minutes % 60} мин`;
}

export function SettingsPage({
  token,
  focusSection,
  focusRequestId,
}: {
  token: string;
  focusSection?: SettingsFocusSection | undefined;
  focusRequestId?: number | undefined;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    city: "",
    citySelectionToken: "",
    address: "",
    addressSelectionToken: "",
    noAddress: false,
    paymentPhone: "",
    paymentBanks: [] as PaymentBank[],
    paymentOtherBankName: "",
    bookingWindowDays: 30,
    featureReminder2h: true,
    reminder2hMinutesBefore: 120,
    reminder2hMessage: "",
    featureReturnReminder: true,
    returnReminderMessage: "",
    masterNotificationChannel: "TELEGRAM" as "TELEGRAM" | "MAX",
  });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [cityTokenAutoResolveMessage, setCityTokenAutoResolveMessage] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [maxBindingCode, setMaxBindingCode] = useState<MaxNotificationBindingCode | null>(null);
  const [maxBindingCopied, setMaxBindingCopied] = useState(false);
  const scheduleSectionRef = useRef<HTMLDivElement | null>(null);
  const notificationsSectionRef = useRef<HTMLDivElement | null>(null);
  const paymentSectionRef = useRef<HTMLDivElement | null>(null);

  const { data: business, isLoading } = useQuery({
    queryKey: ["business-me"],
    queryFn: () => apiFetch<Business>(token, "/api/business/me"),
  });

  useEffect(() => {
    if (!focusSection || focusRequestId === undefined || isLoading) return;

    const target =
      focusSection === "schedule"
        ? scheduleSectionRef.current
        : focusSection === "notifications"
          ? notificationsSectionRef.current
          : paymentSectionRef.current;

    if (!target) return;

    const timeoutId = window.setTimeout(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [focusRequestId, focusSection, isLoading]);

  useEffect(() => {
    if (!business) return;
    setForm({
      name: business.name,
      phone: business.phone ?? "",
      city: business.city ?? "",
      citySelectionToken: business.citySelectionToken ?? "",
      address: business.address ?? "",
      addressSelectionToken: business.addressSelectionToken ?? "",
      noAddress: business.locationStatus === "NO_ADDRESS" || !business.address,
      paymentPhone: business.paymentPhone ?? "",
      paymentBanks: normalizePaymentBanks(business.paymentBanks, business.paymentBank),
      paymentOtherBankName: business.paymentOtherBankName ?? "",
      bookingWindowDays: business.bookingWindowDays ?? 30,
      featureReminder2h: business.featureReminder2h,
      reminder2hMinutesBefore: business.reminder2hMinutesBefore ?? 120,
      reminder2hMessage: business.reminder2hMessage ?? "",
      featureReturnReminder: business.featureReturnReminder,
      returnReminderMessage: business.returnReminderMessage ?? "",
      masterNotificationChannel: business.masterNotificationChannel ?? "TELEGRAM",
    });
  }, [business]);

  useEffect(() => {
    const savedCity = business?.city ?? null;
    const savedCitySelectionToken = business?.citySelectionToken ?? null;
    const shouldResolve = needsSavedCityTokenResolution({
      savedCity,
      savedCitySelectionToken,
      currentCity: form.city,
      currentCitySelectionToken: form.citySelectionToken,
    });

    if (!shouldResolve || !savedCity) {
      setCityTokenAutoResolveMessage("");
      return;
    }

    const controller = new AbortController();
    const url = new URL(`${API_BASE}/api/geo/cities`, window.location.origin);
    url.searchParams.set("q", savedCity.trim());
    setCityTokenAutoResolveMessage("Подбираем сохраненный город из справочника...");

    fetch(url.toString(), { signal: controller.signal })
      .then(async (res) => {
        const json = (await res.json()) as {
          ok: boolean;
          data?: { items?: GeoSuggestion[] };
          error?: string;
        };
        if (!res.ok || !json.ok) {
          throw new Error(json.error ?? "Не удалось проверить город");
        }
        return json.data?.items ?? [];
      })
      .then((items) => {
        const matched = pickExactCitySuggestion(items, savedCity);
        if (!matched) {
          setCityTokenAutoResolveMessage(
            "Чтобы изменить адрес, сначала выберите город из подсказки. После этого поле адреса откроется.",
          );
          return;
        }

        setForm((current) => {
          if (
            !needsSavedCityTokenResolution({
              savedCity,
              savedCitySelectionToken,
              currentCity: current.city,
              currentCitySelectionToken: current.citySelectionToken,
            })
          ) {
            return current;
          }

          return {
            ...current,
            city: matched.label,
            citySelectionToken: matched.token,
          };
        });
        setCityTokenAutoResolveMessage("");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setCityTokenAutoResolveMessage(
          err instanceof Error
            ? `Не удалось проверить сохраненный город: ${err.message}. Выберите город из подсказки вручную.`
            : "Не удалось проверить сохраненный город. Выберите город из подсказки вручную.",
        );
      });

    return () => controller.abort();
  }, [business?.city, business?.citySelectionToken, form.city, form.citySelectionToken]);

  const businessNoAddress = business ? business.locationStatus === "NO_ADDRESS" || !business.address : form.noAddress;
  const hasLocationChanges = Boolean(
    business &&
      (form.city !== (business.city ?? "") ||
        form.address !== (business.address ?? "") ||
        form.noAddress !== businessNoAddress),
  );
  const hasValidLocationSelection =
    Boolean(form.citySelectionToken) && (form.noAddress || Boolean(form.addressSelectionToken));
  const canSave = !hasLocationChanges || hasValidLocationSelection;

  const setLocationField = (key: "city" | "address", value: string) => {
    setError("");
    setSaved(false);
    setForm((current) => {
      if (key === "city") {
        return {
          ...current,
          city: value,
          citySelectionToken: "",
          address: "",
          addressSelectionToken: "",
        };
      }
      return {
        ...current,
        address: value,
        addressSelectionToken: "",
        noAddress: false,
      };
    });
  };

  const selectCity = (item: GeoSuggestion) => {
    setError("");
    setSaved(false);
    setForm((current) => ({
      ...current,
      city: item.label,
      citySelectionToken: item.token,
      address: "",
      addressSelectionToken: "",
    }));
  };

  const selectAddress = (item: GeoSuggestion) => {
    setError("");
    setSaved(false);
    setForm((current) => ({
      ...current,
      address: item.label,
      addressSelectionToken: item.token,
      noAddress: false,
    }));
  };

  const togglePaymentBank = (bank: PaymentBank) => {
    setSaved(false);
    setForm((current) => {
      const nextBanks = current.paymentBanks.includes(bank)
        ? current.paymentBanks.filter((item) => item !== bank)
        : [...current.paymentBanks, bank];

      return {
        ...current,
        paymentBanks: nextBanks,
        paymentOtherBankName: nextBanks.includes("OTHER") ? current.paymentOtherBankName : "",
      };
    });
  };

  const selectMasterNotificationChannel = (channel: "TELEGRAM" | "MAX") => {
    setError("");
    setSaved(false);
    setForm((current) => ({ ...current, masterNotificationChannel: channel }));
  };

  const setPasswordField = (key: keyof typeof passwordForm, value: string) => {
    setPasswordError("");
    setPasswordSaved(false);
    setPasswordForm((current) => ({ ...current, [key]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        name: form.name,
        phone: form.phone || undefined,
        paymentPhone: form.paymentPhone || undefined,
        paymentBanks: form.paymentBanks,
        paymentOtherBankName: form.paymentBanks.includes("OTHER") ? form.paymentOtherBankName : "",
        bookingWindowDays: form.bookingWindowDays,
        featureReminder2h: form.featureReminder2h,
        reminder2hMinutesBefore: form.reminder2hMinutesBefore,
        reminder2hMessage: form.reminder2hMessage.trim(),
        featureReturnReminder: form.featureReturnReminder,
        returnReminderMessage: form.returnReminderMessage.trim(),
        masterNotificationChannel: form.masterNotificationChannel,
      };

      if (hasLocationChanges) {
        payload["city"] = form.city.trim();
        payload["citySelectionToken"] = form.citySelectionToken;
        if (form.noAddress) {
          payload["noAddress"] = true;
        } else {
          payload["address"] = form.address.trim();
          payload["addressSelectionToken"] = form.addressSelectionToken;
        }
      }

      await apiFetch(token, "/api/business/me", {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["business-me"] });
      setSaved(true);
      setError("");
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (e: Error) => setError(e.message),
  });

  const passwordMutation = useMutation({
    mutationFn: async () => {
      if (passwordForm.newPassword.length < 8) {
        throw new Error("Новый пароль должен быть не короче 8 символов");
      }
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        throw new Error("Новый пароль и повтор не совпадают");
      }

      await apiFetch<{ changed: boolean }>(token, "/api/staff/me/password", {
        method: "POST",
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
    },
    onSuccess: () => {
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordSaved(true);
      setPasswordError("");
      setTimeout(() => setPasswordSaved(false), 3000);
    },
    onError: (e: Error) => {
      setPasswordSaved(false);
      setPasswordError(e.message);
    },
  });

  const maxBindingMutation = useMutation({
    mutationFn: () =>
      apiFetch<MaxNotificationBindingCode>(token, "/api/staff/me/max-notification-binding-code", {
        method: "POST",
    }),
    onSuccess: (data) => {
      setMaxBindingCode(data);
      setMaxBindingCopied(false);
      setError("");
    },
    onError: (e: Error) => setError(e.message),
  });

  if (isLoading) {
    return <div className="p-4 text-center text-gray-400 py-10">Загрузка...</div>;
  }

  const maxBindingDeepLink =
    maxBindingCode && MAX_BOT_URL
      ? buildUrlWithParams(MAX_BOT_URL, { start: maxBindingCode.payload })
      : null;
  const maxBindingExpiresAt = maxBindingCode
    ? new Date(maxBindingCode.expiresAt).toLocaleTimeString("ru", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  const preVisitPreviewMessage = form.reminder2hMessage.trim() || DEFAULT_PRE_VISIT_REMINDER_MESSAGE;
  const returnPreviewMessage = form.returnReminderMessage.trim() || DEFAULT_RETURN_REMINDER_MESSAGE;
  const telegramConnected = business?.masterNotificationBindings?.telegram?.connected ?? false;
  const maxConnected = business?.masterNotificationBindings?.max?.connected ?? false;
  const currentNotificationLabel = form.masterNotificationChannel === "MAX" ? "MAX" : "Telegram";
  const currentNotificationConnected =
    form.masterNotificationChannel === "MAX" ? maxConnected : telegramConnected;
  const canChangePassword = Boolean(
    passwordForm.currentPassword && passwordForm.newPassword && passwordForm.confirmPassword,
  );

  return (
    <div className="mx-auto w-full max-w-lg min-w-0 p-4">
      <h2 className="text-lg font-bold text-gray-800 mb-1">Настройки записи</h2>
      <p className="text-sm leading-5 text-gray-500 mb-3">
        Контакты, адрес, уведомления, оплата и правила онлайн-записи.
      </p>

      <div
        id="settings-schedule"
        ref={scheduleSectionRef}
        className="mb-4 space-y-4 scroll-mt-4"
      >
        <ScheduleSettingsSection token={token} timeZone={business?.timeZone ?? DEFAULT_BUSINESS_TIME_ZONE} />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          passwordMutation.mutate();
        }}
        className="mb-4"
      >
        <Section title="Безопасность">
          <p className="text-xs leading-5 text-gray-400 -mt-1">
            Пароль используется для входа в кабинет и админ-бот.
          </p>
          <Field label="Текущий пароль">
            <input
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordField("currentPassword", e.target.value)}
              className="input"
              autoComplete="current-password"
              required
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Новый пароль">
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordField("newPassword", e.target.value)}
                className="input"
                autoComplete="new-password"
                minLength={8}
                required
              />
            </Field>
            <Field label="Повторите пароль">
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordField("confirmPassword", e.target.value)}
                className="input"
                autoComplete="new-password"
                minLength={8}
                required
              />
            </Field>
          </div>

          {passwordError && (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-500">{passwordError}</p>
          )}
          {passwordSaved && (
            <p className="rounded-xl bg-green-50 px-3 py-2 text-sm text-green-600">
              Пароль изменён
            </p>
          )}

          <button
            type="submit"
            disabled={passwordMutation.isPending || !canChangePassword}
            className="w-full rounded-xl bg-gray-900 py-3 font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50"
          >
            {passwordMutation.isPending ? "Сохраняем..." : "Сменить пароль"}
          </button>
        </Section>
      </form>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSave) {
            setError("Выберите город и адрес из подсказок или отметьте работу без фиксированного адреса.");
            return;
          }
          saveMutation.mutate();
        }}
        className="space-y-4"
      >
        {/* Основная информация */}
        <Section title="Основная информация">
          <Field label="Название мастера / студии *">
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input"
              placeholder="Анна Иванова"
            />
          </Field>
          <Field label="Телефон">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="input"
              placeholder="+7 900 000 00 00"
            />
          </Field>
        </Section>

        {/* Уведомления мастеру */}
        <Section
          id="settings-notifications"
          sectionRef={notificationsSectionRef}
          title="Куда присылать служебные уведомления"
        >
          <p className="text-xs leading-5 text-gray-400 -mt-1">
            Новые записи, отмены, переносы и напоминания приходят в выбранный бот. Это видно только мастеру.
            Клиенты не видят этот выбор.
          </p>
          <div className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-700">
            <span className="font-semibold">Личная служебная ссылка мастера.</span>{" "}
            Не отправляйте эту ссылку клиентам: клиентские ссылки находятся во вкладке «Ссылки».
          </div>

          <div className="rounded-xl border border-primary/10 bg-primary/5 px-3 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-primary/70">
                Текущий канал
              </span>
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  currentNotificationConnected
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700"
                }`}
              >
                {currentNotificationConnected ? "Подключен" : "Не подключен"}
              </span>
            </div>
            <p className="mt-1 text-sm font-semibold text-gray-800">{currentNotificationLabel}</p>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              Номер телефона здесь не выбирается. Телефон нужен только для входа в кабинет и бот.
              Чтобы получать уведомления в другом аккаунте, откройте нужный бот именно с этого аккаунта и подключите его.
            </p>
          </div>

          <div className="grid gap-3">
            <div
              className={`rounded-xl border p-3 transition ${
                form.masterNotificationChannel === "TELEGRAM"
                  ? "border-primary/40 bg-primary/5"
                  : "border-gray-100 bg-white"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Telegram</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">
                    Уведомления придут в Telegram-аккаунт, который вошел в админ-бот Запись24.
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    telegramConnected
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {telegramConnected ? "Подключен" : "Не подключен"}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {TELEGRAM_ADMIN_BOT_URL && (
                  <a
                    href={TELEGRAM_ADMIN_BOT_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-9 items-center rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary/90"
                  >
                    Открыть Telegram-бот
                  </a>
                )}
                {telegramConnected && (
                  <button
                    type="button"
                    onClick={() => selectMasterNotificationChannel("TELEGRAM")}
                    disabled={form.masterNotificationChannel === "TELEGRAM"}
                    className="inline-flex min-h-9 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-primary/60 disabled:cursor-default disabled:opacity-60"
                  >
                    {form.masterNotificationChannel === "TELEGRAM" ? "Выбран" : "Выбрать Telegram"}
                  </button>
                )}
              </div>
              {!telegramConnected && (
                <p className="mt-2 text-xs leading-5 text-amber-700">
                  В боте войдите по телефону и паролю от кабинета, тогда статус станет подключенным.
                </p>
              )}
            </div>

            <div
              className={`rounded-xl border p-3 transition ${
                form.masterNotificationChannel === "MAX"
                  ? "border-primary/40 bg-primary/5"
                  : "border-gray-100 bg-white"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">MAX</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">
                    Свяжите MAX с кабинетом один раз. Это подключает личный чат мастера для уведомлений.
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    maxConnected
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {maxConnected ? "Подключен" : "Не подключен"}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {maxConnected ? (
                  <button
                    type="button"
                    onClick={() => selectMasterNotificationChannel("MAX")}
                    disabled={form.masterNotificationChannel === "MAX"}
                    className="inline-flex min-h-9 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-primary/60 disabled:cursor-default disabled:opacity-60"
                  >
                    {form.masterNotificationChannel === "MAX" ? "Выбран" : "Выбрать MAX"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => maxBindingMutation.mutate()}
                    disabled={maxBindingMutation.isPending}
                    className="inline-flex min-h-9 items-center rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-60"
                  >
                    {maxBindingMutation.isPending ? "Готовим..." : "Подключить MAX"}
                  </button>
                )}
                {MAX_BOT_URL && (
                  <a
                    href={maxBindingDeepLink ?? MAX_BOT_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-9 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-primary/60"
                  >
                    Открыть MAX-бот
                  </a>
                )}
              </div>

              {maxBindingCode && (
                <div className="mt-3 grid gap-3 rounded-xl border border-yellow-100 bg-yellow-50 p-3 text-xs text-gray-600 sm:grid-cols-[1fr_auto] sm:items-center">
                  <div className="space-y-2">
                    <p className="text-gray-600">
                      Код действует до {maxBindingExpiresAt}.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-lg tracking-[0.2em] text-gray-900 select-all">
                        {maxBindingCode.code}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          void navigator.clipboard?.writeText(maxBindingCode.code);
                          setMaxBindingCopied(true);
                        }}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-600 hover:border-primary/50"
                      >
                        {maxBindingCopied ? "Скопировано" : "Скопировать"}
                      </button>
                    </div>
                  </div>
                  {maxBindingDeepLink && (
                    <QRCodeSVG
                      value={maxBindingDeepLink}
                      size={96}
                      level="M"
                      marginSize={4}
                      title="QR для подключения MAX"
                      role="img"
                      aria-label="QR для подключения MAX"
                      className="h-24 w-24 rounded-lg border border-gray-100 bg-white"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-500">
            Выбранный чат получает уведомления мастера.
          </p>
        </Section>

        {/* Напоминания клиентам */}
        <Section title="Напоминания клиентам">
          <p className="text-xs leading-5 text-gray-400 -mt-1">
            Эти тексты получают клиенты.
          </p>

          <div className="space-y-3 border-b border-gray-100 pb-4">
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.featureReminder2h}
                onChange={(e) => setForm({ ...form, featureReminder2h: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>
                <span className="block font-semibold text-gray-800">Перед записью</span>
                <span className="block text-xs leading-5 text-gray-500">
                  Напоминание отправится активным клиентам за выбранное время до визита.
                </span>
              </span>
            </label>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Когда отправлять
              </p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {REMINDER_TIME_OPTIONS.map((option) => (
                  <button
                    key={option.minutes}
                    type="button"
                    onClick={() => setForm({ ...form, reminder2hMinutesBefore: option.minutes })}
                    className={`min-h-10 rounded-lg border px-2 text-sm font-semibold transition ${
                      form.reminder2hMinutesBefore === option.minutes
                        ? "border-primary bg-primary text-white"
                        : "border-gray-200 bg-white text-gray-600 hover:border-primary/60"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <Field label="Текст перед записью">
              <textarea
                value={form.reminder2hMessage}
                onChange={(e) => setForm({ ...form, reminder2hMessage: e.target.value })}
                className="input min-h-24 resize-y leading-5"
                maxLength={500}
                placeholder={DEFAULT_PRE_VISIT_REMINDER_MESSAGE}
              />
              <div className="mt-1 flex items-start justify-between gap-3 text-xs text-gray-400">
                <span>Дата, время, услуга и мастер добавятся автоматически.</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, reminder2hMessage: "" })}
                  className="shrink-0 font-semibold text-primary hover:text-primary/80"
                >
                  По умолчанию
                </button>
              </div>
            </Field>

            <div className="rounded-xl bg-gray-50 px-3 py-3 text-xs leading-5 text-gray-600">
              <p className="mb-2 font-semibold uppercase tracking-wide text-gray-400">Как увидит клиент</p>
              <p className="font-semibold text-gray-800">⏰ Напоминание о записи</p>
              <p className="mt-2">{DEFAULT_PRE_VISIT_REMINDER_INTRO}:</p>
              <p>📅 Сегодня, 11:30</p>
              <p>💼 Услуга</p>
              <p>👤 Мастер</p>
              <p className="mt-2 whitespace-pre-wrap">{preVisitPreviewMessage}</p>
              <p className="mt-2 text-gray-400">
                Сейчас выбрано: за {minutesToLabel(form.reminder2hMinutesBefore)} до записи.
              </p>
            </div>
          </div>

          <div className="space-y-3 pt-1">
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.featureReturnReminder}
                onChange={(e) => setForm({ ...form, featureReturnReminder: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>
                <span className="block font-semibold text-gray-800">Возврат клиента</span>
                <span className="block text-xs leading-5 text-gray-500">
                  Используется, когда в завершенной записи нажимают «Напомнить вернуться».
                </span>
              </span>
            </label>

            <Field label="Текст возвратного напоминания">
              <textarea
                value={form.returnReminderMessage}
                onChange={(e) => setForm({ ...form, returnReminderMessage: e.target.value })}
                className="input min-h-24 resize-y leading-5"
                maxLength={500}
                placeholder={DEFAULT_RETURN_REMINDER_MESSAGE}
              />
              <div className="mt-1 flex items-start justify-between gap-3 text-xs text-gray-400">
                <span>Кнопка записи добавится к сообщению автоматически.</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, returnReminderMessage: "" })}
                  className="shrink-0 font-semibold text-primary hover:text-primary/80"
                >
                  По умолчанию
                </button>
              </div>
            </Field>

            <div className="rounded-xl bg-gray-50 px-3 py-3 text-xs leading-5 text-gray-600">
              <p className="mb-2 font-semibold uppercase tracking-wide text-gray-400">Как увидит клиент</p>
              <p className="whitespace-pre-wrap">{returnPreviewMessage}</p>
              <p className="mt-2 text-gray-400">Под сообщением будет кнопка «Записаться».</p>
            </div>
          </div>
        </Section>

        {/* Адрес */}
        <Section title="Местоположение">
          <GeoSuggestInput
            label="Город"
            value={form.city}
            onChange={(value) => setLocationField("city", value)}
            onSelect={selectCity}
            selectedToken={form.citySelectionToken}
            kind="city"
            placeholder="Омск"
            required
          />
          {cityTokenAutoResolveMessage && (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-700">
              {cityTokenAutoResolveMessage}
            </p>
          )}
          {!form.noAddress && (
            <GeoSuggestInput
              label="Адрес"
              value={form.address}
              onChange={(value) => setLocationField("address", value)}
              onSelect={selectAddress}
              selectedToken={form.addressSelectionToken}
              kind="address"
              cityToken={form.citySelectionToken}
              disabled={!form.citySelectionToken}
              placeholder={form.citySelectionToken ? "ул. Ленина, 5" : "Сначала выберите город"}
              required
            />
          )}
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={form.noAddress}
              onChange={(e) => {
                const checked = e.target.checked;
                setError("");
                setSaved(false);
                setForm((current) => ({
                  ...current,
                  noAddress: checked,
                  address: checked ? "" : current.address,
                  addressSelectionToken: checked ? "" : current.addressSelectionToken,
                }));
              }}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            Без фиксированного адреса / работаю на выезде
          </label>
          {hasLocationChanges && !hasValidLocationSelection && (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-700">
              Выберите город из подсказки, а для фиксированного кабинета — адрес с номером дома.
            </p>
          )}
        </Section>

        {/* Оплата через СБП */}
        <Section
          id="settings-payment"
          sectionRef={paymentSectionRef}
          title="Оплата (СБП)"
        >
          <p className="text-xs text-gray-400 -mt-1 mb-2">
            Клиент увидит реквизиты СБП и список банков в деталях своей записи.
          </p>
          <Field label="Телефон для СБП">
            <input
              type="tel"
              value={form.paymentPhone}
              onChange={(e) => setForm({ ...form, paymentPhone: e.target.value })}
              className="input"
              placeholder="+7 900 000 00 00"
            />
          </Field>
          <Field label="Банки получателя">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {PAYMENT_BANKS.map((bank) => (
                <label
                  key={bank}
                  className="flex min-h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={form.paymentBanks.includes(bank)}
                    onChange={() => togglePaymentBank(bank)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>{PAYMENT_BANK_LABELS[bank]}</span>
                </label>
              ))}
            </div>
            {form.paymentBanks.includes("OTHER") && (
              <input
                value={form.paymentOtherBankName}
                onChange={(e) => setForm({ ...form, paymentOtherBankName: e.target.value })}
                className="input mt-2"
                placeholder="Название банка"
              />
            )}
          </Field>
        </Section>

        {/* Запись */}
        <Section title="Запись">
          <Field label="Окно записи — на сколько дней вперёд клиент может записаться">
            <select
              value={form.bookingWindowDays}
              onChange={(e) => setForm({ ...form, bookingWindowDays: Number(e.target.value) })}
              className="input bg-white"
            >
              <option value={7}>7 дней</option>
              <option value={14}>14 дней</option>
              <option value={30}>30 дней</option>
              <option value={60}>60 дней</option>
              <option value={90}>90 дней</option>
              <option value={180}>180 дней</option>
              <option value={365}>1 год</option>
            </select>
          </Field>
        </Section>

        {error && (
          <p className="text-red-500 text-sm bg-red-50 rounded-xl px-3 py-2">{error}</p>
        )}

        {saved && (
          <p className="text-green-600 text-sm bg-green-50 rounded-xl px-3 py-2">
            ✓ Настройки сохранены
          </p>
        )}

        <button
          type="submit"
          disabled={saveMutation.isPending || !canSave}
          className="w-full bg-primary text-white py-3 rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50"
        >
          {saveMutation.isPending ? "Сохранение..." : "Сохранить изменения"}
        </button>
      </form>
    </div>
  );
}

function GeoSuggestInput({
  label,
  value,
  selectedToken,
  onChange,
  onSelect,
  placeholder,
  required = false,
  kind,
  cityToken,
  disabled = false,
}: {
  label: string;
  value: string;
  selectedToken: string;
  onChange: (value: string) => void;
  onSelect: (item: GeoSuggestion) => void;
  placeholder?: string;
  required?: boolean;
  kind: "city" | "address";
  cityToken?: string;
  disabled?: boolean;
}) {
  const [items, setItems] = useState<GeoSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const query = value.trim();
    const needsCity = kind === "address" && !cityToken;
    if (disabled || needsCity || selectedToken || !focused || query.length < 2) {
      setItems([]);
      setLoading(false);
      setMessage("");
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      const url = new URL(
        `${API_BASE}/api/geo/${kind === "city" ? "cities" : "addresses"}`,
        window.location.origin,
      );
      url.searchParams.set("q", query);
      if (kind === "address" && cityToken) {
        url.searchParams.set("cityToken", cityToken);
      }

      setLoading(true);
      setMessage("");
      fetch(url.toString(), { signal: controller.signal })
        .then(async (res) => {
          const json = (await res.json()) as {
            ok: boolean;
            data?: { items?: GeoSuggestion[] };
            error?: string;
          };
          if (!res.ok || !json.ok) {
            throw new Error(json.error ?? "Не удалось загрузить подсказки");
          }
          setItems(json.data?.items ?? []);
          setOpen(true);
          setMessage(
            json.data?.items?.length
              ? ""
              : kind === "address"
                ? "Ничего не найдено. Введите улицу и номер дома"
                : "Ничего не найдено",
          );
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setItems([]);
          setMessage(err instanceof Error ? err.message : "Не удалось загрузить подсказки");
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [cityToken, disabled, focused, kind, selectedToken, value]);

  return (
    <div className="relative">
      <label className="mb-1 block text-xs text-gray-500">
        {label}
        {required && <span className="text-primary"> *</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setFocused(true);
          setOpen(true);
        }}
        onBlur={() => {
          setFocused(false);
          window.setTimeout(() => setOpen(false), 120);
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={`input ${selectedToken ? "border-green-300 focus:border-green-400" : ""} disabled:cursor-not-allowed disabled:opacity-60`}
      />
      {selectedToken && (
        <p className="mt-1 text-xs font-semibold text-green-600">Выбрано из справочника</p>
      )}
      {!selectedToken && message && (
        <p className="mt-1 text-xs text-amber-700">{message}</p>
      )}
      {loading && !selectedToken && (
        <p className="mt-1 text-xs text-primary">Ищем...</p>
      )}
      {open && items.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
          {items.slice(0, 6).map((item) => (
            <button
              key={item.token}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (kind === "address" && item.precision !== "house") {
                  onChange(item.label);
                  setMessage("Добавьте номер дома для точного адреса");
                } else {
                  onSelect(item);
                  setMessage("");
                }
                setOpen(false);
                setItems([]);
              }}
              className="block w-full border-0 border-b border-gray-100 bg-white px-3 py-2.5 text-left transition last:border-b-0 hover:bg-gray-50"
            >
              <span className="block text-sm font-semibold text-gray-800">{item.label}</span>
              {(item.description || (kind === "address" && item.precision === "street")) && (
                <span className="mt-0.5 block text-xs leading-5 text-gray-500">
                  {kind === "address" && item.precision === "street"
                    ? `${item.description ? `${item.description} · ` : ""}добавьте номер дома`
                    : item.description}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  children,
  id,
  sectionRef,
}: {
  title: string;
  children: React.ReactNode;
  id?: string;
  sectionRef?: Ref<HTMLDivElement>;
}) {
  return (
    <div
      id={id}
      ref={sectionRef}
      className="min-w-0 scroll-mt-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
    >
      <h3 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
